<template>
  <section
    :aria-labelledby="headingId"
    class="ProgramElements"
  >
    <BaseInvisible
      :id="headingId"
      tag="h3"
    >Program Elements</BaseInvisible>
    <transition-group
      v-if="currentElements"
      ref="products"
      :move-class="moveClass"
      tag="ul"
      class="ProgramElements__elements"
    >
      <li
        v-for="(element, index) in currentElements"
        :key="element"
        :aria-hidden="index === 2 ? 'false' : 'true'"
        class="ProgramElements__element"
      >
        <div class="ProgramElements__image-wrapper">
          <BaseImage
            :id="element"
            class="ProgramElements__image"
          />
          <div class="ProgramElements__image-gradient"/>
          <div class="ProgramElements__image-label">
            <div class="ProgramElements__image-number-wrapper">
              <TextEpsilon
                class="ProgramElements__image-number"
                v-text="(elements.indexOf(element) + 1).toString().padStart(2, 0)"
              />
            </div>
            <TextEpsilon v-html="`${name} Elements`"/>
          </div>
        </div>
      </li>
    </transition-group>
    <button
      class="ProgramElements__button ProgramElements__button_back"
      @click="back"
    >
      <BaseArrow
        direction="left"
        class="ProgramElements__arrow"
      />
    </button>
    <button
      class="ProgramElements__button ProgramElements__button_forward"
      @click="forward"
    >
      <BaseArrow
        direction="right"
        class="ProgramElements__arrow"
      />
    </button>
  </section>
</template>

<script>
  import Hammer from 'hammerjs';

  import TextEpsilon from './TextEpsilon';

  export default {
    components: {
      TextEpsilon,
    },
    props: {
      name: {
        type: String,
        required: true,
      },
      elements: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        currentElements: undefined,
        moveClass: undefined,
      };
    },
    computed: {
      headingId() {
        return `heading_${this._uid}`;
      },
    },
    watch: {
      elements: {
        immediate: true,
        handler(elements) {
          this.currentElements = [...elements.slice(-2), ...elements.slice(0, -2)];
        },
      },
    },
    mounted() {
      const hammer = new Hammer(this.$refs.products.$el);

      hammer.on('swiperight', this.back);
      hammer.on('swipeleft', this.forward);
    },
    methods: {
      back() {
        const { currentElements } = this;

        this.moveClass = 'ProgramElements__element_move-back';

        currentElements.unshift(currentElements.pop());
      },
      forward() {
        const { currentElements } = this;

        this.moveClass = 'ProgramElements__element_move-forward';

        currentElements.push(currentElements.shift());
      },
    },
  };
</script>

<style lang="scss">
  .ProgramElements {
    @include breakpoint(phablet) {
      position: relative;
    }
  }

  .ProgramElements__arrow {
    fill: color(white);
    transition-property: fill;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);

    .ProgramElements__button:focus &,
    .ProgramElements__button:hover & {
      fill: color(slate);
    }
  }

  .ProgramElements__button {
    display: none;

    @include breakpoint(phablet) {
      $size: 48px;

      position: absolute;
      top: 50%;
      display: block;
      padding: spacing(base-alpha);
      width: $size;
      height: $size;
      transform: translateY(-50%);
      background-color: color(bronze);

      &:focus,
      &:hover {
        outline-style: none;
      }
    }
  }

  .ProgramElements__button_back {
    left: spacing(base-gamma);
  }

  .ProgramElements__button_forward {
    right: spacing(base-gamma);
  }

  .ProgramElements__element {
    margin-right: calc(200% - #{spacing(base-beta) * 10});
    margin-left: calc(-200% + #{spacing(base-beta) * 10});
    padding-right: spacing(base-beta);
    padding-left: spacing(base-beta);
    width: calc(100% - #{spacing(base-beta) * 4});
    flex-shrink: 0;

    @include breakpoint(phablet) {
      margin-right: 200%;
      margin-left: -200%;
      padding-right: 0;
      padding-left: 0;
      width: 100%;
    }
  }

  .ProgramElements__elements {
    display: flex;
    overflow: hidden;
  }

  .ProgramElements__element_move-back,
  .ProgramElements__element_move-forward {
    transition-property: transform;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);

    @include breakpoint(phablet) {
      transition-duration: duration(slow);
    }
  }

  .ProgramElements__element_move-back {
    &:first-child {
      transition-duration: 1ms;
    }
  }

  .ProgramElements__element_move-forward {
    &:last-child {
      transition-duration: 1ms;
    }
  }

  .ProgramElements__image {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ProgramElements__image-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 25%;
    background-image: linear-gradient(#{color(slate)}, transparent);
  }

  .ProgramElements__image-label {
    position: absolute;
    top: spacing(base-gamma);
    right: spacing(base-gamma);
    left: spacing(base-gamma);
    color: color(white);
    display: flex;
    align-items: center;
  }

  .ProgramElements__image-number {
    color: color(white);
    display: block;
    transform: rotate(45deg);
  }

  .ProgramElements__image-number-wrapper {
    display: block;
    border: border(white);
    transform: rotate(-45deg);
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: spacing(base-gamma);
  }

  .ProgramElements__image-wrapper {
    position: relative;
    padding-top: 100%;
    background-color: color(slate);
  }
</style>
